import React, { useState, useEffect } from 'react';
import userService, { User } from '@/services/userService';

interface Actor {
  id: number;
  name: string;
  email: string;
  type: 'client' | 'fournisseur' | 'transporteur' | 'chauffeur' | 'convoyeur' | 'exportateur' | 'operateur' | 'agent_qualite';
}

interface EmailRecipientSelectorProps {
  weighingDetails: any;
  selectedActors: Actor[];
  selectedUsers: User[];
  manualRecipients: string;
  onActorsChange: (actors: Actor[]) => void;
  onUsersChange: (users: User[]) => void;
  onManualRecipientsChange: (recipients: string) => void;
}

const EmailRecipientSelector: React.FC<EmailRecipientSelectorProps> = ({
  weighingDetails,
  selectedActors,
  selectedUsers,
  manualRecipients,
  onActorsChange,
  onUsersChange,
  onManualRecipientsChange
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Extraire les acteurs de la pesée
  const getWeighingActors = (): Actor[] => {
    const actors: Actor[] = [];

    // Client
    if (weighingDetails?.client_info) {
      actors.push({
        id: weighingDetails.client_info.id,
        name: weighingDetails.client_info.nom,
        email: weighingDetails.client_info.email || '',
        type: 'client'
      });
    } else if (weighingDetails?.client_nom) {
      actors.push({
        id: 1001,
        name: weighingDetails.client_nom,
        email: weighingDetails.client_email || '',
        type: 'client'
      });
    }

    // Fournisseur
    if (weighingDetails?.fournisseur_info) {
      actors.push({
        id: weighingDetails.fournisseur_info.id,
        name: weighingDetails.fournisseur_info.nom,
        email: weighingDetails.fournisseur_info.email || '',
        type: 'fournisseur'
      });
    } else if (weighingDetails?.fournisseur_nom) {
      actors.push({
        id: 1002,
        name: weighingDetails.fournisseur_nom,
        email: weighingDetails.fournisseur_email || '',
        type: 'fournisseur'
      });
    }

    // Exportateur
    if (weighingDetails?.exportateur_info) {
      actors.push({
        id: weighingDetails.exportateur_info.id,
        name: weighingDetails.exportateur_info.nom,
        email: weighingDetails.exportateur_info.email || '',
        type: 'exportateur'
      });
    } else if (weighingDetails?.exportateur_nom) {
      actors.push({
        id: 1003,
        name: weighingDetails.exportateur_nom,
        email: weighingDetails.exportateur_email || '',
        type: 'exportateur'
      });
    }

    // Chauffeur
    if (weighingDetails?.chauffeur_info) {
      actors.push({
        id: weighingDetails.chauffeur_info.id,
        name: `${weighingDetails.chauffeur_info.prenom} ${weighingDetails.chauffeur_info.nom}`,
        email: weighingDetails.chauffeur_info.email || '',
        type: 'chauffeur'
      });
    } else if (weighingDetails?.chauffeur_nom) {
      actors.push({
        id: 1004,
        name: weighingDetails.chauffeur_nom,
        email: weighingDetails.chauffeur_email || '',
        type: 'chauffeur'
      });
    }

    // Transporteur
    if (weighingDetails?.transporteur_info) {
      actors.push({
        id: weighingDetails.transporteur_info.id,
        name: weighingDetails.transporteur_info.nom,
        email: weighingDetails.transporteur_info.email || '',
        type: 'transporteur'
      });
    } else if (weighingDetails?.transporteur_nom) {
      actors.push({
        id: 1005,
        name: weighingDetails.transporteur_nom,
        email: weighingDetails.transporteur_email || '',
        type: 'transporteur'
      });
    }

    // Convoyeur
    if (weighingDetails?.convoyeur_info) {
      actors.push({
        id: weighingDetails.convoyeur_info.id,
        name: weighingDetails.convoyeur_info.nom,
        email: weighingDetails.convoyeur_info.email || '',
        type: 'convoyeur'
      });
    } else if (weighingDetails?.convoyeur_nom) {
      actors.push({
        id: 1006,
        name: weighingDetails.convoyeur_nom,
        email: weighingDetails.convoyeur_email || '',
        type: 'convoyeur'
      });
    }

    // Ajouter des acteurs par défaut si aucun acteur n'est trouvé
    if (actors.length === 0) {
      actors.push({
        id: 999,
        name: 'Opérateur de Pesée',
        email: 'operateur@cajou.ci',
        type: 'operateur'
      });

      actors.push({
        id: 998,
        name: 'Agent Qualité',
        email: 'qualite@cajou.ci',
        type: 'agent_qualite'
      });
    }

    return actors;
  };

  // Charger les utilisateurs du site
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await userService.getSiteUsers();
        setUsers(response.results || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Rechercher des utilisateurs
  const searchUsers = async (query: string) => {
    if (query.length < 2) return;
    
    setLoading(true);
    try {
      const response = await userService.searchUsers(query);
      setUsers(response.results || []);
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Gérer la sélection d'un acteur
  const handleActorToggle = (actor: Actor) => {
    const isSelected = selectedActors.some(a => a.id === actor.id && a.type === actor.type);
    
    if (isSelected) {
      onActorsChange(selectedActors.filter(a => !(a.id === actor.id && a.type === actor.type)));
    } else {
      onActorsChange([...selectedActors, actor]);
    }
  };

  // Gérer la sélection d'un utilisateur
  const handleUserToggle = (user: User) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    
    if (isSelected) {
      onUsersChange(selectedUsers.filter(u => u.id !== user.id));
    } else {
      onUsersChange([...selectedUsers, user]);
    }
  };

  // Obtenir l'icône pour le type d'acteur
  const getActorIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      client: 'fas fa-building',
      fournisseur: 'fas fa-truck',
      transporteur: 'fas fa-shipping-fast',
      chauffeur: 'fas fa-user-tie',
      convoyeur: 'fas fa-shield-alt',
      exportateur: 'fas fa-globe',
      operateur: 'fas fa-weight-hanging',
      agent_qualite: 'fas fa-clipboard-check'
    };
    return icons[type] || 'fas fa-user';
  };

  // Obtenir le libellé français pour le type d'acteur
  const getActorLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      client: 'Client',
      fournisseur: 'Fournisseur',
      transporteur: 'Transporteur',
      chauffeur: 'Chauffeur',
      convoyeur: 'Convoyeur',
      exportateur: 'Exportateur',
      operateur: 'Opérateur',
      agent_qualite: 'Agent Qualité'
    };
    return labels[type] || type;
  };

  // Obtenir la couleur pour le type d'acteur
  const getActorColor = (type: string) => {
    const colors: { [key: string]: string } = {
      client: 'text-blue-600',
      fournisseur: 'text-green-600',
      transporteur: 'text-purple-600',
      chauffeur: 'text-orange-600',
      convoyeur: 'text-red-600',
      exportateur: 'text-indigo-600',
      operateur: 'text-pink-600',
      agent_qualite: 'text-teal-600'
    };
    return colors[type] || 'text-gray-600';
  };

  const weighingActors = getWeighingActors();

  return (
    <div className="space-y-6">
      {/* Acteurs de la pesée */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <i className="fas fa-users text-blue-500 mr-2"></i>
          Acteurs de la pesée ({weighingActors.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {weighingActors.map((actor) => {
            const isSelected = selectedActors.some(a => a.id === actor.id && a.type === actor.type);
            return (
              <div
                key={`${actor.type}-${actor.id}`}
                onClick={() => handleActorToggle(actor)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <i className={`${getActorIcon(actor.type)} text-sm`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${getActorColor(actor.type)}`}>
                        {actor.name}
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {getActorLabel(actor.type)}
                      </span>
                    </div>
                    {actor.email && (
                      <p className="text-sm text-gray-600">{actor.email}</p>
                    )}
                  </div>
                  {isSelected && (
                    <i className="fas fa-check-circle text-blue-500"></i>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Utilisateurs du site */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <i className="fas fa-user-friends text-green-500 mr-2"></i>
          Utilisateurs du site ({users.length})
        </h3>
        
        {/* Barre de recherche */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchUsers(e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Rechercher un utilisateur..."
          />
        </div>

        {/* Liste des utilisateurs */}
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Chargement...
            </div>
          ) : users.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Aucun utilisateur trouvé
            </div>
          ) : (
            users.map((user) => {
              const isSelected = selectedUsers.some(u => u.id === user.id);
              return (
                <div
                  key={user.id}
                  onClick={() => handleUserToggle(user)}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <i className="fas fa-user text-sm"></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </span>
                        <span className="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded">
                          Utilisateur
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {user.email} • @{user.username}
                      </div>
                    </div>
                    {isSelected && (
                      <i className="fas fa-check-circle text-green-500"></i>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Destinataires manuels */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <i className="fas fa-edit text-purple-500 mr-2"></i>
          Destinataires manuels
        </h3>
        <textarea
          value={manualRecipients}
          onChange={(e) => onManualRecipientsChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          rows={3}
          placeholder="Saisissez des adresses email supplémentaires (séparées par des virgules)..."
        />
        <p className="mt-1 text-xs text-gray-500">
          Séparez plusieurs adresses par des virgules
        </p>
      </div>

      {/* Récapitulatif des destinataires sélectionnés */}
      {(selectedActors.length > 0 || selectedUsers.length > 0 || manualRecipients.trim()) && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">
            <i className="fas fa-list-check text-blue-500 mr-2"></i>
            Destinataires sélectionnés ({selectedActors.length + selectedUsers.length + (manualRecipients.trim() ? 1 : 0)})
          </h4>
          <div className="space-y-2">
            {selectedActors.map((actor) => (
              <div key={`${actor.type}-${actor.id}`} className="flex items-center space-x-2 text-sm">
                <i className={`${getActorIcon(actor.type)} ${getActorColor(actor.type)}`}></i>
                <span className="font-medium">{actor.name}</span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {getActorLabel(actor.type)}
                </span>
                {actor.email && (
                  <span className="text-gray-500">({actor.email})</span>
                )}
              </div>
            ))}
            {selectedUsers.map((user) => (
              <div key={user.id} className="flex items-center space-x-2 text-sm">
                <i className="fas fa-user text-green-600"></i>
                <span className="font-medium">{user.first_name} {user.last_name}</span>
                <span className="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded">
                  Utilisateur
                </span>
                <span className="text-gray-500">({user.email})</span>
              </div>
            ))}
            {manualRecipients.trim() && (
              <div className="flex items-center space-x-2 text-sm">
                <i className="fas fa-edit text-purple-600"></i>
                <span className="font-medium">Destinataires manuels</span>
                <span className="text-xs text-gray-500 bg-purple-100 px-2 py-1 rounded">
                  Manuel
                </span>
                <span className="text-gray-500">({manualRecipients})</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailRecipientSelector;
